import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useBooks, type BrowseSort } from "../lib/books";
import { translateGenre } from "../lib/book-display";
import { BookCover } from "../components/BookCover";
import "./browse.css";

export function Browse() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(
    () => ({
      q: searchParams.get("q") ?? "",
      genre: searchParams.get("genre") ?? "",
      author: searchParams.get("author") ?? "",
      sort: searchParams.get("sort") ?? "",
      page: Number(searchParams.get("page") ?? "1") || 1,
    }),
    [searchParams],
  );

  // Empty string maps to the backend default ("newest"); we keep it out of the
  // URL so default browsing produces clean links.
  const SORT_OPTIONS: BrowseSort[] = ["newest", "oldest", "title", "author"];

  const [qDraft, setQDraft] = useState(params.q);
  useEffect(() => setQDraft(params.q), [params.q]);

  const query = useBooks({
    q: params.q || undefined,
    genre: params.genre || undefined,
    author: params.author || undefined,
    sort: (params.sort as BrowseSort) || undefined,
    page: params.page,
  });

  function update(next: Record<string, string | number | undefined>) {
    const sp = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === "" || v === 0) sp.delete(k);
      else sp.set(k, String(v));
    }
    if (!("page" in next)) sp.delete("page");
    setSearchParams(sp, { replace: false });
  }

  const data = query.data;

  return (
    <div className="container browse">
      <header className="browse__head">
        <h1>{t("browse.title")}</h1>
        <form
          className="browse__search"
          onSubmit={(e) => {
            e.preventDefault();
            update({ q: qDraft });
          }}
          role="search"
          aria-label={t("browse.title")}
        >
          <label htmlFor="q" className="sr-only">
            {t("browse.searchLabel")}
          </label>
          <input
            id="q"
            type="search"
            placeholder={t("browse.searchPlaceholder")}
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
          />
          <button type="submit" className="btn btn-primary btn-sm">
            {t("browse.submit")}
          </button>
        </form>
        <div className="browse__sort">
          <label htmlFor="sort">{t("browse.sortLabel")}</label>
          <select
            id="sort"
            value={params.sort}
            onChange={(e) => update({ sort: e.target.value })}
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s} value={s === "newest" ? "" : s}>
                {t(`browse.sort_${s}`)}
              </option>
            ))}
          </select>
        </div>
      </header>

      {data && data.facets.genres.length > 0 && (
        <div className="chip-row" role="group" aria-label={t("browse.genreLabel")}>
          <button
            type="button"
            className={`chip ${params.genre === "" ? "chip--active" : ""}`}
            onClick={() => update({ genre: "" })}
          >
            {t("browse.all")}
          </button>
          {data.facets.genres.map((g) => (
            <button
              key={g}
              type="button"
              className={`chip ${params.genre === g ? "chip--active" : ""}`}
              onClick={() => update({ genre: params.genre === g ? "" : g })}
            >
              {translateGenre(t, g)}
            </button>
          ))}
        </div>
      )}

      <p className="browse__summary" aria-live="polite">
        {query.isPending
          ? t("browse.loading")
          : data
            ? t("browse.summary", { count: data.total })
            : t("browse.empty")}
      </p>

      {query.isError && (
        <div className="surface" role="alert">
          <p>{t("browse.errorLoading")}</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul className="book-grid">
          {data.items.map((book) => (
            <li key={book.id} className="book-card">
              <Link to={`/book/${book.id}`} className="book-card__link">
                <div className="book-card__cover">
                  <BookCover
                    bookId={book.id}
                    hasCover={Boolean(book.coverUrl)}
                    title={book.title}
                    author={book.author}
                    alt={`${book.title}${book.author ? ` — ${book.author}` : ""}`}
                  />
                </div>
                <div className="book-card__body">
                  <h2 className="book-card__title">{book.title}</h2>
                  {book.author && (
                    <p className="book-card__author">{book.author}</p>
                  )}
                  {book.genre && (
                    <span className="book-card__genre">{translateGenre(t, book.genre)}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {data && data.items.length === 0 && !query.isPending && (
        <div className="surface" style={{ textAlign: "center" }}>
          <p>{t("browse.empty")}</p>
        </div>
      )}

      {data && data.pages > 1 && (
        <nav className="browse__pages" aria-label={t("browse.title")}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={params.page <= 1}
            onClick={() => update({ page: params.page - 1 })}
          >
            {t("browse.previous")}
          </button>
          <span aria-current="page">
            {t("browse.pageOf", { page: params.page, total: data.pages })}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={params.page >= data.pages}
            onClick={() => update({ page: params.page + 1 })}
          >
            {t("browse.next")}
          </button>
        </nav>
      )}
    </div>
  );
}
